//@flow
import * as Immutable from 'immutable';

import { TimeSlot } from 'common/models/flow/TimeSlot';
import type { TimeSlotModelType } from 'common/models/flow/TimeSlot';

import {
    validatable,
    validate,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';
import TimeUtils from 'common/utils/TimeUtils';
import DataUtils from 'common/utils/DataUtils';
import moment from 'moment-timezone';
import I18nHelper from 'common/helpers/I18nHelper';
import { DayOfWeekType } from 'common/enums/DateTimeEnums';
import { MultiDaySlot } from 'common/models/internal/MultiDaySlot';

const translationsNamespace = ['validations'];

const nsTranslate = I18nHelper.translate.bind(null, translationsNamespace);

@validatable
class InternalTimeSlot extends TimeSlot {
    static fromJS(json: TimeSlotModelType): InternalTimeSlot {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    @validateAll
    setAllValidationErrors(): this {
        return this;
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        return false;
    }

    @validate
    setStart(start: string): this {
        return this.clone(this._state.set('start', start));
    }

    validateStart() {
        const { startMoment, endMoment } = this.toMoment();
        if (!DataUtils.isEmpty(this.start) && !startMoment.isValid()) {
            return nsTranslate('timeSlot.invalidStartTime');
        }
        const endError = this.validateEnd();
        if (endError) {
            return endError;
        }
        if (
            !DataUtils.isEmpty(this.start) &&
            !DataUtils.isEmpty(this.end) &&
            TimeUtils.isAfter(
                startMoment,
                endMoment.add(this.get('endDayOffset'), 'd')
            )
        ) {
            return nsTranslate('timeSlot.startTimeAfterEndTime');
        }
    }

    isEmpty(): boolean {
        return DataUtils.isEmpty(this.start) && DataUtils.isEmpty(this.end);
    }

    @validate
    setEnd(end: string): this {
        return this.clone(this._state.set('end', end));
    }

    validateEnd() {
        const { endMoment } = this.toMoment();
        if (!DataUtils.isEmpty(this.end) && !endMoment.isValid()) {
            return nsTranslate('timeSlot.invalidEndTime');
        }
        if (!DataUtils.isEmpty(this.start) && DataUtils.isEmpty(this.end)) {
            return nsTranslate('timeSlot.invalidEndTime');
        }
    }

    setEndDayOffset(endDayOffset: boolean): this {
        return this.clone(this._state.set('endDayOffset', endDayOffset));
    }

    getEndDayOffset(): boolean {
        return this._state.get('endDayOffset');
    }

    isTimeSlotOverlapping = (slots, slotIndex) => {
        const otherSlots = slots.slice(0, slotIndex);
        const { startMoment, endMoment } = this.toMoment();

        const isOverlapping = otherSlots.some(slot => {
            const {
                startMoment: comparingStart,
                endMoment: comparingEnd
            } = this.toMoment(slot);
            return !(
                (startMoment.isBefore(comparingStart) &&
                    endMoment.isBefore(comparingStart)) ||
                (startMoment.isAfter(comparingEnd) &&
                    endMoment.isAfter(comparingEnd))
            );
        });
        return isOverlapping;
    };

    toMoment = (slot: ?TimeSlot) => {
        const { start, end } = slot || this;
        const inputFormat = moment.HTML5_FMT.TIME;
        const startMoment = moment(start, inputFormat);
        const endMoment = moment(end, inputFormat);
        return {
            startMoment,
            endMoment
        };
    };

    static sortTimeSlots(timeSlots: Immutable.List<TimeSlot>) {
        const order = Object.values(DayOfWeekType);
        const sortedSlots = timeSlots.sortBy((slotA, slotB) => {
            return (
                order.indexOf(slotA.dayOfWeek) - order.indexOf(slotB.dayOfWeek)
            );
        });
        return sortedSlots;
    }

    static initializeAllTimeSlots(timeSlots: Immutable.List<TimeSlot>) {
        const days = Object.values(DayOfWeekType);
        const slots = days.map(day => {
            const slot = timeSlots.find(timeSlot => {
                return timeSlot.dayOfWeek === day;
            });
            if (slot) {
                return slot;
            } else {
                return MultiDaySlot.fromJS({
                    dayOfWeek: day,
                    slots: []
                });
            }
        });
        return Immutable.List(slots);
    }

    static expectedStartEndTime(): { start: moment, end: moment } {
        const ROUNDING = 30 * 60 * 1000; /*ms*/
        const now = moment();
        const startTime = moment(Math.ceil(+now / ROUNDING) * ROUNDING);
        const endTime = moment(startTime).add(60, 'minutes');
        return { start: startTime, end: endTime };
    }
}

export { InternalTimeSlot as TimeSlot };
